import type { ReactNode } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { cn } from '@rocco/utils/cn';
import { cva, type VariantProps } from 'class-variance-authority';
import { useAtom } from 'jotai';
import { uiStateAtomReachedFooter } from '@rocco/ui/website-footer/states/ui-states-reached-footer';
import { uiStateAtomReachedProjectPageContent } from '@rocco/ui/website-footer/states/ui-states-reached-project-page-content';

interface FloatingIslandProps
    extends VariantProps<typeof _floatingIslandContentVariants> {
    children: ReactNode;
    scrollThreshold?: number;
    onVisibilityChange?: (isVisible: boolean) => void;
    onClick?: () => void;
    before?: ReactNode;
    // When true, the inner content will be unmounted from the DOM while hidden.
    // The outer wrapper remains mounted so the component can continue to
    // observe scroll/visibility state. This prevents the island from
    // intercepting pointer events while hidden.
    unmountOnHidden?: boolean;
    className?: string;
}

const _floatingIslandVariants = cva(
    'fixed bottom-8 left-1/2 z-sticky w-full -translate-x-1/2 px-[1.6875rem] py-4 md:bottom-10 md:px-0 md:py-0',
    {
        variants: {
            variant: {
                default: 'md:max-w-[56.125rem]',
                narrow: 'md:max-w-[38.375rem]',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    }
);

const _floatingIslandContentVariants = cva(
    'rounded-full bg-[#949494]/20 backdrop-blur-island',
    {
        variants: {
            variant: {
                details: 'p-2 md:pr-6',
                detailsNoImage: 'p-2 pl-7.5 md:pl-13.5 md:pr-6',
                category: 'p-2 md:py-2 md:pl-13 md:pr-2',
                default: 'p-[0.3125rem] md:p-4',
                home: 'p-2 md:py-2 md:pl-[3.1375rem] md:pr-2',
                transparent: 'bg-transparent p-0',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    }
);

export const FloatingIsland = ({
    children,
    scrollThreshold = 200,
    variant = 'default',
    onVisibilityChange,
    onClick,
    before,
    unmountOnHidden = false,
    className,
}: FloatingIslandProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const [reachedFooter] = useAtom(uiStateAtomReachedFooter);
    const [reachedProjectPageContent] = useAtom(
        uiStateAtomReachedProjectPageContent
    );

    const handleScroll = useCallback(() => {
        let frameId: number | null = null;

        return () => {
            if (frameId !== null) {
                cancelAnimationFrame(frameId);
            }

            frameId = requestAnimationFrame(() => {
                const footerBlocker = reachedFooter;
                const projectPageContentBlocker = reachedProjectPageContent;

                // hide the island if we've reached either the footer or the project page content
                const isBlocked = footerBlocker || projectPageContentBlocker;

                const newIsVisible =
                    scrollThreshold === 0
                        ? !isBlocked
                        : window.scrollY > scrollThreshold && !isBlocked;

                setIsVisible((prev) => {
                    if (prev !== newIsVisible) {
                        onVisibilityChange?.(newIsVisible);
                        return newIsVisible;
                    }
                    return prev;
                });
            });
        };
    }, [
        scrollThreshold,
        onVisibilityChange,
        reachedFooter,
        reachedProjectPageContent,
    ]);

    useEffect(() => {
        const scrollListener = handleScroll();
        window.addEventListener('scroll', scrollListener, { passive: true });

        const frameId = requestAnimationFrame(() => {
            if (scrollThreshold === 0) {
                // when threshold is zero, visibility depends on footer/project page content blockers
                const isBlocked = reachedFooter || reachedProjectPageContent;
                setIsVisible(!isBlocked);
                onVisibilityChange?.(!isBlocked);
            } else {
                scrollListener();
            }
        });

        return () => {
            window.removeEventListener('scroll', scrollListener);
            cancelAnimationFrame(frameId);
        };
    }, [
        handleScroll,
        scrollThreshold,
        onVisibilityChange,
        reachedFooter,
        reachedProjectPageContent,
    ]);

    return (
        <div
            className={cn(
                _floatingIslandVariants({ variant: 'default' }),
                // Offset to account for the scrollbar when scrollbar is locked.
                '[body[data-scroll-locked="1"]_&]:translate-x-[calc(-50%-var(--removed-body-scroll-bar-size,14px)/2)]',
                // Prevent the outer wrapper from intercepting pointer events while hidden.
                !isVisible ? 'pointer-events-none' : ''
            )}
        >
            {unmountOnHidden && !isVisible ? null : (
                <div
                    onClick={onClick}
                    className={cn(
                        _floatingIslandContentVariants({ variant }),
                        'transition-[opacity,transform] duration-300 ease-out',
                        isVisible
                            ? 'translate-y-0 opacity-100'
                            : 'translate-y-full opacity-0',
                        className
                    )}
                >
                    {before}
                    {children}
                </div>
            )}
        </div>
    );
};
